import { FilterCondition } from '../components/AdvancedFilter';

/**
 * Apply filters to an array of data items
 * @param data - Array of data items to filter
 * @param filters - Array of filter conditions to apply
 * @returns Filtered array
 */
export function applyFilters<T extends Record<string, any>>(
  data: T[],
  filters: FilterCondition[]
): T[] {
  if (!filters || filters.length === 0) {
    return data;
  }

  return data.filter((item) => {
    // Item must match ALL filters (AND logic)
    return filters.every((filter) => {
      const fieldValue = item[filter.field];
      const filterValue = filter.value;

      // Handle null/undefined field values
      if (fieldValue === null || fieldValue === undefined) {
        return filter.operator === 'notEquals';
      }

      switch (filter.operator) {
        case 'equals': {
          // Support boolean-like values: 1/0, '1'/'0', Yes/No, 'true'/'false'
          const normalizeBool = (v: any): 'true' | 'false' | null => {
            if (v === true || String(v).toLowerCase() === 'true' || String(v).toLowerCase() === 'yes' || String(v) === '1') return 'true';
            if (v === false || String(v).toLowerCase() === 'false' || String(v).toLowerCase() === 'no' || String(v) === '0') return 'false';
            return null;
          };

          const filterBool = normalizeBool(filterValue);
          if (filterBool !== null) {
            const fieldBool = normalizeBool(fieldValue);
            if (fieldBool !== null) return fieldBool === filterBool;
            // If field isn't boolean-like, fallthrough to string compare
          }

          return String(fieldValue).toLowerCase() === String(filterValue).toLowerCase();
        }

        case 'notEquals':
          return String(fieldValue).toLowerCase() !== String(filterValue).toLowerCase();

        case 'contains':
          return String(fieldValue).toLowerCase().includes(String(filterValue).toLowerCase());

        case 'greaterThan':
          return Number(fieldValue) > Number(filterValue);

        case 'lessThan':
          return Number(fieldValue) < Number(filterValue);

        case 'range': {
          const rawMin = filter.value;
          const rawMax = filter.value2;
          const hasMin = rawMin !== undefined && rawMin !== null && rawMin !== '';
          const hasMax = rawMax !== undefined && rawMax !== null && rawMax !== '';
          const min = hasMin ? Number(rawMin) : NaN;
          const max = hasMax ? Number(rawMax) : NaN;
          const numValue = Number(fieldValue);

          if (!hasMin && !hasMax) return true;
          if (!hasMin) {
            // Only max provided: value <= max
            if (isNaN(max)) return true;
            return numValue <= max;
          }
          if (!hasMax) {
            // Only min provided: value >= min
            if (isNaN(min)) return true;
            return numValue >= min;
          }

          // Both min and max provided
          if (isNaN(min) && isNaN(max)) return true;
          if (isNaN(min)) return numValue <= max;
          if (isNaN(max)) return numValue >= min;
          return numValue >= min && numValue <= max;
        }

        case 'in': {
          // Handle comma-separated list
          const values = Array.isArray(filterValue)
            ? filterValue
            : String(filterValue).split(',').map((v) => v.trim());
          
          return values.some(
            (val) => String(fieldValue).toLowerCase() === String(val).toLowerCase()
          );
        }

        default:
          return true;
      }
    });
  });
}

/**
 * Get unique values for a field from a dataset
 * Useful for generating filter options dynamically
 */
export function getUniqueFieldValues<T extends Record<string, any>>(
  data: T[],
  fieldName: string
): Array<{ value: any; label: string }> {
  const uniqueValues = new Set<string>();
  
  data.forEach((item) => {
    const value = item[fieldName];
    if (value !== null && value !== undefined) {
      uniqueValues.add(String(value));
    }
  });

  return Array.from(uniqueValues)
    .sort()
    .map((value) => ({
      value,
      label: value,
    }));
}

/**
 * Generate filter configs from data fields dynamically
 * Useful when you don't know the schema ahead of time
 */
export function generateFilterConfigs<T extends Record<string, any>>(
  sampleData: T[],
  fieldLabels?: Record<string, string>
) {
  if (!sampleData || sampleData.length === 0) {
    return [];
  }

  const firstItem = sampleData[0];
  const configs = Object.keys(firstItem).map((field) => {
    const sampleValue = firstItem[field];
    let type: 'text' | 'number' | 'select' | 'boolean' = 'text';

    // Detect type from sample value
    if (typeof sampleValue === 'number') {
      type = 'number';
    } else if (typeof sampleValue === 'boolean') {
      type = 'boolean';
    }

    return {
      field,
      label: fieldLabels?.[field] || formatFieldName(field),
      type,
      options: undefined, // Can be populated if needed
    };
  });

  return configs;
}

/**
 * Format field name for display (camelCase -> Title Case)
 */
function formatFieldName(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
}
